import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stages = [
  { id: 1, name: "Choc", description: "\"L'IA va me remplacer !\"", x: 5, y: 20 },
  { id: 2, name: "Déni", description: "\"Ça ne marchera jamais vraiment\"", x: 15, y: 35 },
  { id: 3, name: "Colère", description: "\"C'est injuste et dangereux\"", x: 28, y: 75 },
  { id: 4, name: "Marchandage", description: "\"Je vais l'ignorer le plus longtemps possible\"", x: 42, y: 85 },
  { id: 5, name: "Dépression", description: "\"Je ne comprends rien, je suis dépassé\"", x: 55, y: 70 },
  { id: 6, name: "Acceptation", description: "\"C'est un outil, apprenons à l'utiliser\"", x: 72, y: 45 },
  { id: 7, name: "Intégration", description: "\"L'IA augmente mes capacités !\"", x: 90, y: 25 }
];

export const GriefCurve = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const [activeStage, setActiveStage] = useState(null);

  // SVG path for the curve
  const curvePath = "M 30,80 C 80,140 150,340 220,340 S 320,280 380,200 S 480,80 560,100";

  return (
    <div ref={containerRef} className="relative w-full" data-testid="grief-curve-container">
      {/* Header */}
      <div className="text-center mb-12">
        <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
          La Courbe du Deuil face à l'IA
        </h3>
        <p className="text-white/60 max-w-2xl mx-auto">
          Comprendre les étapes émotionnelles que traversent les équipes face à l'adoption de l'IA 
          permet de mieux les accompagner vers l'acceptation et l'intégration.
        </p>
      </div>

      {/* Curve visualization */}
      <div className="relative h-[400px] md:h-[500px] w-full">
        <svg 
          viewBox="0 0 600 400" 
          className="w-full h-full"
          style={{ overflow: "visible" }}
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Curve background glow */}
          <motion.path
            d={curvePath}
            fill="none"
            stroke="rgba(0, 122, 255, 0.2)"
            strokeWidth="40"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
          />

          {/* Main curve */}
          <motion.path
            d={curvePath}
            fill="none"
            stroke="#007AFF"
            strokeWidth="4"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 2, ease: "easeInOut", delay: 0.3 }}
          />

          {/* Stage points */}
          {stages.map((stage, index) => {
            const xPos = (stage.x / 100) * 560 + 20;
            const yPos = (stage.y / 100) * 320 + 40;
            
            return (
              <motion.g
                key={stage.id}
                initial={{ opacity: 0, scale: 0 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                transition={{ delay: 0.5 + index * 0.15, duration: 0.4 }}
                onMouseEnter={() => setActiveStage(stage.id)}
                onMouseLeave={() => setActiveStage(null)}
                style={{ cursor: "pointer" }}
              >
                {/* Outer glow */}
                <circle
                  cx={xPos}
                  cy={yPos}
                  r={activeStage === stage.id ? 20 : 12}
                  fill="rgba(0, 122, 255, 0.2)"
                  style={{ transition: "r 0.3s ease" }}
                />
                {/* Inner dot */}
                <circle
                  cx={xPos}
                  cy={yPos}
                  r={8}
                  fill={activeStage === stage.id ? "#007AFF" : "#0F0F10"}
                  stroke="#007AFF"
                  strokeWidth="2"
                />
                {/* Label */}
                <text
                  x={xPos}
                  y={yPos - 20}
                  textAnchor="middle"
                  fill="white"
                  fontSize="12"
                  fontWeight="600"
                  fontFamily="Outfit, sans-serif"
                >
                  {stage.name}
                </text>
              </motion.g>
            );
          })}

          {/* Axis labels */}
          <text x="300" y="390" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="11" fontFamily="Manrope">
            Temps →
          </text>
          <text x="15" y="200" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="11" fontFamily="Manrope" transform="rotate(-90, 15, 200)">
            Émotion ↑
          </text>
        </svg>

        {/* Tooltip */}
        {activeStage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 glass-card p-4 max-w-xs text-center"
          >
            <p className="text-[#007AFF] font-semibold mb-1">
              {stages.find(s => s.id === activeStage)?.name}
            </p>
            <p className="text-white/70 text-sm italic">
              {stages.find(s => s.id === activeStage)?.description}
            </p>
          </motion.div>
        )}
      </div>

      {/* Stage cards mobile */}
      <div className="md:hidden mt-8 space-y-3">
        {stages.map((stage, index) => (
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="flex items-center gap-4 p-3 glass-card"
          >
            <div className="w-8 h-8 rounded-full bg-[#007AFF]/20 flex items-center justify-center text-[#007AFF] font-bold text-sm">
              {stage.id}
            </div>
            <div>
              <p className="text-white font-medium">{stage.name}</p>
              <p className="text-white/50 text-sm">{stage.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default GriefCurve;
